import { MAX_RICH_TEXT_LENGTH, POLICY_STATUS } from '@ibe/constants';
import { z } from 'zod';

/**
 * The Cancellation Policies module's contracts.
 *
 * Types come from `z.infer`, never hand-written, so a schema and its type
 * cannot drift. Every limit is the one the service enforces, so an over-long
 * name is a sentence beside the input rather than a round trip and a 422 — the
 * server still validates, this is a courtesy.
 */

/**
 * Predefined cancellation policy codes the admin presents as a dropdown.
 *
 * These are the standard codes an operator may choose from. The service still
 * enforces the same pattern, so the only valid submissions are this list.
 */
export const CANCELLATION_POLICY_CODE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'FLEX-24', label: 'Flexible 24-hour' },
  { value: 'MODERATE-48', label: 'Moderate 48-hour' },
  { value: 'STRICT-72', label: 'Strict 72-hour' },
  { value: 'NON-REFUNDABLE', label: 'Non-refundable' },
];

const CODE_VALUES = new Set(CANCELLATION_POLICY_CODE_OPTIONS.map((option) => option.value));

const MAX_NAME = 255;
const MAX_DESCRIPTION = 4000;

/**
 * One row in the list.
 *
 * **Only `id` is guaranteed.** The list sends `?fields=` derived from the
 * columns the reader has chosen and the service honours it strictly, so a
 * field behind a hidden column is genuinely absent. Declaring any other field
 * required would make the whole list fail to parse the moment someone hides a
 * column.
 */
export const cancellationPolicyRow = z.object({
  id: z.string(),
  propertyId: z.string().optional(),
  code: z.string().optional(),
  name: z.string().optional(),
  description: z.string().nullable().optional(),
  status: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type CancellationPolicyRow = z.infer<typeof cancellationPolicyRow>;

/**
 * The full policy, as the detail screen and the edit form read it.
 *
 * Required rather than optional: the detail endpoint is called without
 * `?fields=` and therefore projects everything, so a missing key is a contract
 * change worth failing on.
 */
export const cancellationPolicyDetail = z.object({
  id: z.string(),
  propertyId: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  contentHtml: z.string().nullable(),
  terms: z.record(z.string(), z.unknown()),
  status: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Echoed back so the edit form can round-trip it on save. It is the
  // optimistic lock, not decoration.
  version: z.number(),
});

export type CancellationPolicyDetail = z.infer<typeof cancellationPolicyDetail>;

/** The statuses a policy may be moved between, in the order they read. */
export const POLICY_STATUS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: POLICY_STATUS.ACTIVE, label: 'Active' },
  { value: POLICY_STATUS.INACTIVE, label: 'Inactive' },
  { value: POLICY_STATUS.ARCHIVED, label: 'Archived' },
];

const statusValues = POLICY_STATUS_OPTIONS.map((option) => option.value) as [string, ...string[]];

/**
 * What the create and edit forms collect.
 *
 * One schema for both, because they are the same fields under the same rules;
 * what differs is which of them the service will accept, and that is the
 * payload builder's job rather than a second schema to keep in step. Every
 * value is a string — a controlled input cannot hold `null`.
 */
export const cancellationPolicyFormInput = z.object({
  propertyId: z.string().min(1, 'Choose the property this policy belongs to'),
  code: z
    .string()
    .min(1, 'Select a policy code')
    .refine((value) => CODE_VALUES.has(value), 'Select a policy code from the list'),
  name: z
    .string()
    .trim()
    .min(1, 'A policy needs a name — it is what an operator picks it by')
    .max(MAX_NAME, `Keep the name under ${String(MAX_NAME)} characters`),
  description: z.string().trim().max(MAX_DESCRIPTION, 'That summary is too long'),
  contentHtml: z
    .string()
    .max(MAX_RICH_TEXT_LENGTH, 'That document is too long. Keep it under 65,000 characters.'),
  status: z.enum(statusValues),
});

export type CancellationPolicyFormInput = z.infer<typeof cancellationPolicyFormInput>;

/** What the create endpoint returns. */
export const createdCancellationPolicy = z.object({ id: z.string() });

export type CreatedCancellationPolicy = z.infer<typeof createdCancellationPolicy>;
